import React, { useContext, useState } from 'react';
import {
  Button,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from '@material-ui/core';
import moment from 'moment';
import { FileCopyOutlined } from '@material-ui/icons';
import { AppContext } from '../../../modules/AppContext';
import { Expandable } from '../../../components/Expandable';
import { useAndromeda } from '../../../modules/Andromeda';
import { usePermissions } from '../../../modules/Permissions';
import config from '../../../config';

const useStyles = makeStyles((theme) => ({
  tableContainer: {
    width: '100%',
    padding: theme.spacing(2),
  },
  titleCell: {
    fontWeight: 'bold',
  },
  pointer: {
    cursor: 'pointer',
  },
}));

const typeMap = {
  initialLink: 'Initial Link',
  rebalance: 'Rebalance',
  relink: 'Relink',
};

export const JobInformationTable = ({ data, loading, active }) => {
  const classes = useStyles();
  const { showToast } = useContext(AppContext);
  const { getPlaywrightTrace, getPlaywrightVideo } = useAndromeda();
  const permissions = usePermissions();

  const [traceDownloading, setTraceDownloading] = useState(false);
  const [videoDownloading, setVideoDownloading] = useState(false);

  const infoRows = [
    {
      key: 'id',
      name: 'Id',
      show: true,
      copyable: true,
      formatter: (val) => val,
    },
    {
      key: 'participantId',
      name: 'Participant Id',
      show: true,
      copyable: true,
      formatter: (val) => val,
    },
    {
      key: 'participantName',
      name: 'Participant Name',
      show: true,
      copyable: true,
      formatter: (val) => val,
    },
    {
      key: 'type',
      name: 'Type',
      show: true,
      copyable: true,
      formatter: (val) => typeMap[val],
    },
    {
      key: 'agent',
      name: 'Agent',
      show: true,
      copyable: true,
      formatter: (val) => val,
    },
    {
      key: 'started',
      name: 'Started',
      show: true,
      copyable: false,
      formatter: (val) =>
        val
          ? moment.utc(val).local().format('MM/DD/YYYY, h:mm:ss a')
          : 'Not available',
    },
    {
      key: 'finished',
      name: 'Finished',
      show: true,
      copyable: false,
      formatter: (val) =>
        val
          ? moment.utc(val).local().format('MM/DD/YYYY, h:mm:ss a')
          : 'Not available',
    },
    {
      key: 'goToMoon',
      name: 'Remote Session',
      copyable: false,
      show: active,
      formatter: (val) => (
        <Button href={config.moon.url} target="_blank">
          Go To Moon
        </Button>
      ),
    },
    {
      key: 'playwrightTraceAvailable',
      name: 'Playwrite Trace',
      show: permissions.hasAndromedaComplianceAccess,
      copyable: false,
      formatter: (val) => (
        <Button
          disabled={!val || traceDownloading}
          onClick={async () => {
            setTraceDownloading(true);
            showToast({
              type: 'info',
              message: 'Downloading Playwright trace...',
            });
            const { success, message } = await getPlaywrightTrace(data?.id);
            if (!success) {
              showToast({
                type: 'error',
                message: message,
              });
              return;
            }
            setTraceDownloading(false);
            showToast({
              type: 'success',
              message: 'Playwright trace downloaded successfully!',
            });
          }}
        >
          {!!val ? 'Download' : 'Not available'}
        </Button>
      ),
    },
    {
      key: 'playwrightTraceAvailable',
      name: 'Playwrite Video',
      show: permissions.hasAndromedaComplianceAccess,
      copyable: false,
      formatter: (val) => (
        <Button
          disabled={!val || videoDownloading}
          onClick={async () => {
            setVideoDownloading(true);
            showToast({
              type: 'info',
              message: 'Downloading Playwright video...',
            });
            const { success, message } = await getPlaywrightVideo(data?.id);
            if (!success) {
              showToast({
                type: 'error',
                message: message,
              });
              return;
            }
            setVideoDownloading(false);
            showToast({
              type: 'success',
              message: 'Playwright video downloaded successfully!',
            });
          }}
        >
          {!!val ? 'Download' : 'Not available'}
        </Button>
      ),
    },
  ];

  return (
    <Expandable defaultToOpen title="Job Information" loading={loading}>
      {/* This table shoudl probably be made into a component since it's being used in multiple places - it's essentially a horizontal datatable component */}
      <TableContainer className={classes.tableContainer}>
        <Table className={classes.table} padding="normal">
          <TableBody>
            {infoRows
              .filter((x) => x.show)
              .map((row, idx) => (
                <TableRow key={idx}>
                  <TableCell className={classes.titleCell}>
                    <Typography variant="body1">
                      <strong>{row.name}</strong>
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary">
                      {row.formatter(data[row.key])}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {row.copyable && (
                      <FileCopyOutlined
                        className={classes.pointer}
                        color="primary"
                        onClick={() => {
                          navigator.clipboard.writeText(data[row.key]);
                          showToast({
                            type: 'info',
                            message: `${row.name} copied to clipboard!`,
                          });
                        }}
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Expandable>
  );
};
